import React, { useMemo, useRef, useState } from 'react';
import { Box, styled, Tab, Tabs, useTheme } from '@mui/material';
import { CATEGORY_TAB_WIDTH } from '../../../_constants/styleConst';
import { useSelector } from 'react-redux';
import { RootState } from '../../../stores/store';
import {
  CLICK_DRAG_THRESHOLD,
  DEFAULT_DRAG_INFO,
} from '../../../_constants/Const';

type TypeTabsProps = {
  changeType: (newCategoryId: string, newTypeId: string) => void;
};

type InspectionTabProps = {
  index: number;
  isError: boolean;
};

/** 中項目タブ一覧の表示制御 */
const InspectionTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    display: 'none',
  },
});

/** 中項目タブ1つ分 */
const InspectionTab = styled(Tab, {
  shouldForwardProp: (prop) => prop !== 'index' && prop !== 'isError',
})<InspectionTabProps>(({ index, isError }) => {
  const theme = useTheme();
  return {
    marginRight: '-17px',
    width: '180px',
    height: '30px',
    padding: '0 24px',
    backgroundColor: isError
      ? theme.palette.error.light
      : theme.palette.secondary.main,
    color: isError
      ? theme.palette.error.contrastText
      : theme.palette.secondary.contrastText,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.caption.fontWeight,
    borderRadius: index === 0 ? '8px' : 0,
    clipPath:
      index === 0
        ? 'polygon(0% 5%, 0% 50%, 0% 95%, 90% 95%, 100% 50%, 90% 5%)'
        : 'polygon(0% 5%, 10% 50%, 0% 95%, 90% 95%, 100% 50%, 90% 5%)',

    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      opacity: '1',
      color: theme.palette.common.white,
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '&:not(.Mui-selected):hover': {
      backgroundColor: isError
        ? theme.palette.error.dark
        : theme.palette.secondary.dark,
    },
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 3,
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'pre-wrap',
  };
});

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

/** 中項目タブ一覧 */
const TypeTabs: React.FC<TypeTabsProps> = ({ changeType }) => {
  const { categoryId, typeId } = useSelector(
    (state: RootState) => state.selectIds,
  );
  const { inspectionTypeDataMap } = useSelector(
    (state: RootState) => state.inspectionTypeData,
  );
  const { typeLabelsMap } = useSelector((state: RootState) => state.typeLabels);

  const categoryData = useMemo(
    () => inspectionTypeDataMap[categoryId],
    [categoryId, inspectionTypeDataMap],
  );
  const typeList = useMemo(
    () => typeLabelsMap[categoryId] || [],
    [categoryId, typeLabelsMap],
  );
  // タブのDOM要素の参照
  const tabsRef = useRef<HTMLDivElement | null>(null);
  // マウスの開始位置とスクロール位置を保持（レンダリングをトリガーしないようrefを使用）
  const dragRef = useRef(DEFAULT_DRAG_INFO);
  // ドラッグ中（スクロール中）か
  const [isDragging, setIsDragging] = useState(false);

  /** ドラッグ終了時の処理 */
  const resetDragStatus = () => {
    setIsDragging(false);
    dragRef.current = DEFAULT_DRAG_INFO;
  };

  /** 中項目タブクリック時の処理 */
  const onClickTab = (newTypeIndex: string) => {
    if (!tabsRef.current) {
      return;
    }
    // ドラッグ中ではない、またはスクロールを伴わないドラッグであれば処理を実行
    if (
      !isDragging ||
      dragRef.current.scrollLeft === tabsRef.current.scrollLeft
    ) {
      changeType(categoryId, typeList[Number(newTypeIndex)].id);
    }
    // ドラッグ状態をリセット
    resetDragStatus();
  };

  /** ポインター押下時の処理 */
  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!tabsRef.current) {
      return;
    }

    // ドラッグ開始時の座標を保持
    dragRef.current = {
      x: event.clientX,
      scrollLeft: tabsRef.current.scrollLeft,
      isMoving: false, // まだ「移動」はしていない
    };
  };

  /** ポインター移動時の処理 */
  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    // マウス押下中でなければ（初期状態ならば）処理しない
    if (dragRef.current.x === DEFAULT_DRAG_INFO.x) {
      return;
    }
    // ドラッグ中のマウスの移動量
    const moveX = event.clientX - dragRef.current.x;
    // 閾値を超えて動いた場合のみ、ドラッグ中とみなす
    if (!dragRef.current.isMoving && Math.abs(moveX) > CLICK_DRAG_THRESHOLD) {
      dragRef.current.isMoving = true;
      setIsDragging(true);
      return;
    }
    // ドラッグ中はスクロールを実行
    if (dragRef.current.isMoving && tabsRef.current) {
      event.preventDefault();
      tabsRef.current.scrollLeft = dragRef.current.scrollLeft - moveX;
    }
  };

  /** ポインターアップ時の処理 */
  const handlePointerUp = () => {
    // dragRefのマウスの座標を初期化してpointerMoveの処理を止める
    // scrollLeft、isDraggingはonClickTabで使用するため初期化しない
    dragRef.current.x = DEFAULT_DRAG_INFO.x;
  };

  const isError = (typeId: string) => {
    if (!categoryData) {
      return false;
    }
    if (!categoryData[typeId]) {
      return false;
    }
    return categoryData[typeId].status === 1;
  };

  return (
    <Box
      style={{
        position: 'absolute',
        width: `calc(100% - (${CATEGORY_TAB_WIDTH} + 2rem))`,
      }}
      ref={tabsRef}
      // PCとタブレットの両方に対応するためonPointerを使用
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={resetDragStatus}
      sx={{
        touchAction: 'none',
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}
    >
      <InspectionTabs
        value={Math.max(
          typeList.findIndex((type) => type.id === typeId),
          0,
        )}
        aria-label="inspection tabs"
        variant="standard"
        sx={{ minWidth: 'max-content' }}
      >
        {typeList.map((type, index) => {
          return (
            <InspectionTab
              key={`type_tab_${index}`}
              isError={isError(type.id)}
              label={type.label}
              index={index}
              onClick={() => onClickTab(index.toString())}
              {...a11yProps(index)}
              sx={[index === typeList.length - 1 ? { mr: '20px' } : {}]}
            />
          );
        })}
      </InspectionTabs>
    </Box>
  );
};

export default React.memo(TypeTabs);
